import type { ComponentType } from 'react';
import BadgeList from 'src/components/badge-list';
import { notEmpty } from 'src/utilityTypes';

import type { CustomFieldValue } from '@/utils/table/types';
import { matchToArrayField } from '@/utils/table/utils/customAttributeHelpers';

import type { CustomAttributeProps } from '../renderers/field-layouts/CustomAttributeProps';
import { CustomAttributeTagsMultiSelect } from '../renderers/field-layouts/CustomAttributeTagsMultiSelect';
import { getBasicFieldConfig } from './defaults';
import { type FieldTypeConfig } from './types';

export const tags: FieldTypeConfig = {
  i18nKey: 'customAttributes.fieldTypes.tags',
  FieldComponent:
    CustomAttributeTagsMultiSelect as unknown as ComponentType<CustomAttributeProps>,

  getTableFieldConfig: (renderProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customFieldValue: CustomFieldValue<any> = (
      { CustomAttributeData },
      { tagTypeLookup }
    ) => {
      const tagTypeIds = matchToArrayField(
        CustomAttributeData,
        renderProps.path
      );

      return tagTypeIds
        ? tagTypeIds.map((id) => tagTypeLookup?.[id] ?? null).filter(notEmpty)
        : [];
    };

    return {
      ...getBasicFieldConfig(renderProps),
      customFieldValue,
      isTagLookupRequired: true,
      filterOptions: {
        filteringProperties: {
          operators: [
            {
              operator: '=',
              match: (rowValues: unknown, filterValue: string) =>
                Array.isArray(rowValues) && rowValues.includes(filterValue),
            },
          ],
        },
        filteringOptions: (records) => {
          const names = Array.from(
            new Set(
              records.flatMap((data) =>
                matchToArrayField(data, renderProps.path)
              )
            )
          );

          return names.map((name) => ({
            value: name,
            label: name,
          }));
        },
      },
      cell: (data) => {
        const tagNames = matchToArrayField(data, renderProps.path);

        return <BadgeList badges={tagNames} />;
      },
      exportVal: (data) => matchToArrayField(data, renderProps.path).join(', '),
    };
  },
  getConditionalPropertyFilterProperty: (_, { tagTypes }) => {
    return {
      operators: [
        {
          operator: '=',
          format: (value: string) =>
            tagTypes.find((tag) => tag.TagTypeId === value)?.Name ?? value,
          match: (rowValues: unknown, filterValue: string) =>
            Array.isArray(rowValues) && rowValues.includes(filterValue),
        },
      ],
    };
  },
  getConditionalPropertyFilterOptions: (_, { tagTypes }) => {
    return tagTypes.map((value) => ({
      value: value.TagTypeId,
      label: value.Name,
    }));
  },
  getCustomDataSourceFieldDefinition(renderProps) {
    return {
      defaultLabel: renderProps.label,
      displayType: 'badgeList',
      dataType: 'textArray',
    };
  },
  allowAsConditionSource: true,
};
